import { UnitDef, UnitConversion } from '../../api/types.js';
import { listUnits, createUnit, updateUnit, deleteUnit, getUnitConversions, addConversion, deleteConversion } from '../../api/units.js';
import { unitCache } from '../../unit-cache.js';

/** Built-in unit IDs that cannot be deleted */
const BUILTIN_IDS = new Set(['builtin_kilos', 'builtin_liters', 'builtin_units']);

/**
 * UnitEditor modal dialog for managing units and their conversion factors.
 *
 * Follows the pattern of EditDialog.ts and InlineEntityEditor.ts.
 * Opens a modal that shows all units with their conversions,
 * allows editing names/abbreviations, and creating new units.
 */
export class UnitEditor {
  private modalElement: HTMLElement | null = null;
  private units: UnitDef[] = [];
  private conversions: Map<string, UnitConversion[]> = new Map();
  private allUnits: UnitDef[] = [];
  private resolveClose: (() => void) | null = null;

  /**
   * Open the unit editor dialog.
   * Returns a promise that resolves when the dialog is closed.
   */
  async open(): Promise<void> {
    await this.loadData();
    this.createModal();
    return new Promise((resolve) => {
      this.resolveClose = resolve;
    });
  }

  /**
   * Load all units and their conversions from the API.
   */
  private async loadData(): Promise<void> {
    this.units = await listUnits();
    this.allUnits = [...this.units];

    // Load conversions for each unit
    this.conversions.clear();
    for (const unit of this.units) {
      try {
        const convs = await getUnitConversions(unit.id);
        this.conversions.set(unit.id, convs);
      } catch {
        this.conversions.set(unit.id, []);
      }
    }
  }

  /**
   * Create and display the modal dialog.
   */
  private createModal(): void {
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50';
    this.modalElement.style.backdropFilter = 'blur(2px)';

    const modalContent = document.createElement('div');
    modalContent.className = 'bg-[#1a1a2e] border border-[var(--accent-alpha-20)] rounded-lg p-6 max-w-xl w-full mx-4 shadow-xl max-h-[85vh] overflow-y-auto';

    // Header
    const header = document.createElement('div');
    header.className = 'flex items-center justify-between mb-4';
    header.innerHTML = `
      <h2 class="text-xl font-bold text-[var(--accent)] font-mono">⚙ Gestiona les unitats</h2>
      <button id="unit-editor-close" class="text-[#888] hover:text-white text-2xl leading-none">&times;</button>
    `;
    modalContent.appendChild(header);

    // Close button handler
    header.querySelector('#unit-editor-close')!.addEventListener('click', () => this.close());

    // Units list
    const listContainer = document.createElement('div');
    listContainer.className = 'space-y-2 mb-4';
    listContainer.id = 'unit-editor-list';
    modalContent.appendChild(listContainer);

    // Add new unit button
    const addBtn = document.createElement('button');
    addBtn.className = 'w-full px-4 py-2 text-sm border border-dashed border-[var(--accent-alpha-40)] rounded-md text-[var(--accent)] hover:bg-[var(--accent-alpha-08)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono transition-colors';
    addBtn.textContent = '+ Nova unitat';
    addBtn.addEventListener('click', () => this.showNewUnitForm(listContainer, addBtn));
    modalContent.appendChild(addBtn);

    // Render the units list
    this.renderUnitList(listContainer);

    // Close on click outside
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.close();
    });

    // Close on Escape
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', onKeyDown);

    // Cleanup keydown listener on close
    const origClose = this.close.bind(this);
    const cleanup = () => {
      document.removeEventListener('keydown', onKeyDown);
    };
    // Override close to also cleanup
    this.close = () => {
      cleanup();
      origClose();
    };

    this.modalElement.appendChild(modalContent);
    document.body.appendChild(this.modalElement);
  }

  /**
   * Render the list of units.
   */
  private renderUnitList(container: HTMLElement): void {
    container.innerHTML = '';

    for (const unit of this.units) {
      const row = this.createUnitRow(unit);
      container.appendChild(row);
    }
  }

  /**
   * Create a single unit row with name, abbreviation, and actions.
   */
  private createUnitRow(unit: UnitDef): HTMLElement {
    const row = document.createElement('div');
    row.className = 'border border-[var(--accent-alpha-13)] rounded-md p-3 bg-[#0d1117]';
    row.id = `unit-row-${unit.id}`;

    const isBuiltin = BUILTIN_IDS.has(unit.id);

    // Header row: editable fields + actions
    const headerRow = document.createElement('div');
    headerRow.className = 'flex items-center gap-2';

    // Singular name input
    const singularInput = document.createElement('input');
    singularInput.type = 'text';
    singularInput.className = 'flex-1 px-2 py-1 text-sm bg-transparent border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)] font-mono';
    singularInput.value = unit.singular;
    singularInput.placeholder = 'Singular';

    // Abbreviation input (smaller)
    const abbrInput = document.createElement('input');
    abbrInput.type = 'text';
    abbrInput.className = 'w-16 px-2 py-1 text-sm bg-transparent border border-[#333] rounded text-[var(--accent)] focus:outline-none focus:border-[var(--accent)] font-mono text-center';
    abbrInput.value = unit.abbreviation ?? '';
    abbrInput.placeholder = 'Abbr.';

    // Plural input (hidden initially, shown on expand)
    const pluralInput = document.createElement('input');
    pluralInput.type = 'text';
    pluralInput.className = 'flex-1 px-2 py-1 text-sm bg-transparent border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)] font-mono';
    pluralInput.value = unit.plural;
    pluralInput.placeholder = 'Plural';
    pluralInput.style.display = 'none';

    // Edit/Save button
    const editBtn = document.createElement('button');
    editBtn.className = 'px-3 py-1 text-base text-[var(--accent)] hover:text-[var(--accent-dark)] focus:outline-none font-mono';
    editBtn.textContent = '✎';

    // Delete button (hidden for built-in)
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'px-2 py-1 text-sm text-[#666] hover:text-red-400 focus:outline-none font-mono';
    deleteBtn.textContent = '×';
    if (isBuiltin) {
      deleteBtn.style.display = 'none';
    }

    // Toggle edit mode
    let isEditing = false;
    const toggleEdit = () => {
      isEditing = !isEditing;
      singularInput.readOnly = !isEditing;
      abbrInput.readOnly = !isEditing;
      pluralInput.style.display = isEditing ? '' : 'none';
      editBtn.textContent = isEditing ? '✓' : '✎';
      if (!isEditing) {
        // Save changes
        this.saveUnit(unit.id, singularInput.value, pluralInput.value, abbrInput.value);
      }
    };

    editBtn.addEventListener('click', toggleEdit);

    // Allow Enter to save while editing
    [singularInput, abbrInput, pluralInput].forEach(input => {
      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && isEditing) {
          toggleEdit();
        }
      });
    });

    deleteBtn.addEventListener('click', async () => {
      if (confirm(`Segur que vols eliminar la unitat "${unit.singular}"?`)) {
        try {
          await deleteUnit(unit.id);
          unitCache.invalidate();
          await this.loadData();
          const listContainer = document.getElementById('unit-editor-list');
          if (listContainer) this.renderUnitList(listContainer);
        } catch (err) {
          console.error('Failed to delete unit:', err);
          alert('Error en eliminar la unitat');
        }
      }
    });

    headerRow.appendChild(singularInput);
    headerRow.appendChild(abbrInput);
    headerRow.appendChild(pluralInput);
    headerRow.appendChild(editBtn);
    headerRow.appendChild(deleteBtn);

    row.appendChild(headerRow);

    // Conversions section
    const conversionsForUnit = this.conversions.get(unit.id) || [];
    const conversionsSection = document.createElement('div');
    conversionsSection.className = 'ml-4 mt-2 space-y-1';
    conversionsSection.id = `conversions-${unit.id}`;

    // Render existing conversions
    this.renderConversions(conversionsSection, unit, conversionsForUnit);

    // Add conversion button
    const addConvBtn = document.createElement('button');
    addConvBtn.className = 'text-xs text-[#888] hover:text-[var(--accent)] focus:outline-none font-mono px-1';
    addConvBtn.textContent = '+ Afegeix conversió';
    addConvBtn.addEventListener('click', () => {
      this.showAddConversionForm(conversionsSection, unit, addConvBtn);
    });
    conversionsSection.appendChild(addConvBtn);

    row.appendChild(conversionsSection);

    return row;
  }

  /**
   * Render a list of conversions for a unit.
   */
  private renderConversions(
    container: HTMLElement,
    unit: UnitDef,
    conversions: UnitConversion[]
  ): void {
    // Remove existing conversion entries (keep the add button)
    const existing = container.querySelectorAll('.conversion-row');
    existing.forEach(el => el.remove());

    for (const conv of conversions) {
      const toUnit = this.allUnits.find(u => u.id === conv.toUnitId);
      const toUnitName = toUnit?.plural ?? conv.toUnitId;

      const convRow = document.createElement('div');
      convRow.className = 'conversion-row flex items-center gap-2 text-xs text-[#a0a0a0] font-mono py-0.5';

      const label = document.createElement('span');
      label.textContent = `1 ${unit.singular} = ${conv.factor} ${toUnitName}`;
      convRow.appendChild(label);

      const deleteConvBtn = document.createElement('button');
      deleteConvBtn.className = 'text-[#666] hover:text-red-400 focus:outline-none px-1';
      deleteConvBtn.textContent = '−';
      deleteConvBtn.addEventListener('click', async () => {
        try {
          await deleteConversion(unit.id, conv.id);
          unitCache.invalidate();
          await this.loadData();
          const listContainer = document.getElementById('unit-editor-list');
          if (listContainer) this.renderUnitList(listContainer);
        } catch (err) {
          console.error('Failed to delete conversion:', err);
        }
      });
      convRow.appendChild(deleteConvBtn);

      container.insertBefore(convRow, container.lastChild);
    }
  }

  /**
   * Show an inline form to add a new conversion.
   */
  private showAddConversionForm(
    container: HTMLElement,
    unit: UnitDef,
    addBtn: HTMLElement
  ): void {
    addBtn.style.display = 'none';

    const formRow = document.createElement('div');
    formRow.className = 'conversion-row flex items-center gap-2 text-xs font-mono py-1';

    const label = document.createElement('span');
    label.className = 'text-[#888]';
    label.textContent = `1 ${unit.singular} = `;
    formRow.appendChild(label);

    // Factor input
    const factorInput = document.createElement('input');
    factorInput.type = 'number';
    factorInput.className = 'w-20 px-1 py-0.5 text-sm bg-transparent border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)]';
    factorInput.placeholder = 'Factor';
    factorInput.step = '0.001';
    factorInput.min = '0';
    formRow.appendChild(factorInput);

    // Unit select (other units except self)
    const unitSelect = document.createElement('select');
    unitSelect.className = 'px-1 py-0.5 text-sm bg-[#0d1117] border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)]';
    const emptyOpt = document.createElement('option');
    emptyOpt.value = '';
    emptyOpt.textContent = '— unitat —';
    unitSelect.appendChild(emptyOpt);
    for (const other of this.allUnits) {
      if (other.id === unit.id) continue;
      const opt = document.createElement('option');
      opt.value = other.id;
      opt.textContent = `${other.plural} (${other.abbreviation})`;
      unitSelect.appendChild(opt);
    }
    formRow.appendChild(unitSelect);

    // Save button
    const saveBtn = document.createElement('button');
    saveBtn.className = 'px-2 py-0.5 text-xs bg-[var(--accent)] text-white rounded hover:bg-[var(--accent-dark)] focus:outline-none';
    saveBtn.textContent = '✓';
    formRow.appendChild(saveBtn);

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'px-2 py-0.5 text-xs text-[#888] hover:text-white focus:outline-none';
    cancelBtn.textContent = '✗';
    formRow.appendChild(cancelBtn);

    saveBtn.addEventListener('click', async () => {
      const factor = parseFloat(factorInput.value);
      const toUnitId = unitSelect.value;
      if (isNaN(factor) || factor <= 0 || !toUnitId) {
        alert('Introdueix un factor vàlid i selecciona una unitat');
        return;
      }
      try {
        await addConversion(unit.id, { toUnitId, factor });
        unitCache.invalidate();
        await this.loadData();
        const listContainer = document.getElementById('unit-editor-list');
        if (listContainer) this.renderUnitList(listContainer);
      } catch (err) {
        console.error('Failed to add conversion:', err);
        alert('Error en afegir la conversió');
      }
    });

    cancelBtn.addEventListener('click', () => {
      formRow.remove();
      addBtn.style.display = '';
    });

    container.insertBefore(formRow, container.lastChild);
    factorInput.focus();
  }

  /**
   * Save a unit's changes.
   */
  private async saveUnit(unitId: string, singular: string, plural: string, abbreviation: string): Promise<void> {
    try {
      await updateUnit(unitId, { singular, plural, abbreviation });
      unitCache.invalidate();
    } catch (err) {
      console.error('Failed to update unit:', err);
      alert('Error en guardar la unitat');
    }
  }

  /**
   * Show an inline form to create a new unit.
   */
  private showNewUnitForm(container: HTMLElement, addBtn: HTMLElement): void {
    addBtn.style.display = 'none';

    const formRow = document.createElement('div');
    formRow.className = 'border border-[var(--accent-alpha-33)] rounded-md p-3 bg-[#0d1117] space-y-2';

    // Singular
    const singularInput = document.createElement('input');
    singularInput.type = 'text';
    singularInput.className = 'w-full px-2 py-1 text-sm bg-transparent border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)] font-mono';
    singularInput.placeholder = 'Singular (ex: quilo)';
    formRow.appendChild(singularInput);

    // Plural
    const pluralInput = document.createElement('input');
    pluralInput.type = 'text';
    pluralInput.className = 'w-full px-2 py-1 text-sm bg-transparent border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)] font-mono';
    pluralInput.placeholder = 'Plural (ex: quilos)';
    formRow.appendChild(pluralInput);

    // Abbreviation
    const abbrInput = document.createElement('input');
    abbrInput.type = 'text';
    abbrInput.className = 'w-full px-2 py-1 text-sm bg-transparent border border-[#333] rounded text-[#e0e0e0] focus:outline-none focus:border-[var(--accent)] font-mono';
    abbrInput.placeholder = 'Abreviació (ex: kg)';
    formRow.appendChild(abbrInput);

    // Buttons row
    const btnRow = document.createElement('div');
    btnRow.className = 'flex gap-2';

    const saveBtn = document.createElement('button');
    saveBtn.className = 'px-3 py-1 text-sm bg-[var(--accent)] text-white rounded hover:bg-[var(--accent-dark)] focus:outline-none font-mono';
    saveBtn.textContent = 'Crea unitat';
    btnRow.appendChild(saveBtn);

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'px-3 py-1 text-sm text-[#888] hover:text-white focus:outline-none font-mono';
    cancelBtn.textContent = 'Cancel·la';
    btnRow.appendChild(cancelBtn);

    formRow.appendChild(btnRow);

    container.insertBefore(formRow, addBtn.nextSibling);

    singularInput.focus();

    saveBtn.addEventListener('click', async () => {
      const singular = singularInput.value.trim();
      const plural = pluralInput.value.trim();
      const abbreviation = abbrInput.value.trim();
      if (!singular || !plural) {
        alert('El singular i el plural són obligatoris');
        return;
      }
      try {
        await createUnit({ singular, plural, abbreviation });
        unitCache.invalidate();
        await this.loadData();
        formRow.remove();
        addBtn.style.display = '';
        const listContainer = document.getElementById('unit-editor-list');
        if (listContainer) this.renderUnitList(listContainer);
      } catch (err) {
        console.error('Failed to create unit:', err);
        alert('Error en crear la unitat');
      }
    });

    cancelBtn.addEventListener('click', () => {
      formRow.remove();
      addBtn.style.display = '';
    });
  }

  /**
   * Close the dialog and cleanup.
   */
  private close(): void {
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
    }
    if (this.resolveClose) {
      this.resolveClose();
      this.resolveClose = null;
    }
  }
}
